import React from 'react';
import { Box, Typography, keyframes, useTheme } from '@mui/material';

interface TranscribingPillProps {
  /**
   * Optional starting timestamp (ms epoch). When provided, the pill
   * renders an MM:SS counter that ticks once per second from that point.
   * Defaults to "now" so the timer starts when the pill is first
   * rendered.
   */
  startedAt?: number;
}

const dotPulse = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

function formatElapsed(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Replaces the chat input contents while the voice message is being
 * transcribed. Shows three pulsing dots, the AC copy, and an elapsed
 * counter. Keeps the parent input row's outer chrome intact so the
 * layout doesn't jump.
 */
export function TranscribingPill({ startedAt }: TranscribingPillProps) {
  const theme = useTheme();
  // Lazy initialisers run once at mount, not on every render, so
  // Date.now() is never called during render and satisfies react-hooks/purity.
  const [start] = React.useState(() => startedAt ?? Date.now());
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsedSec = Math.max(0, Math.floor((now - start) / 1000));

  return (
    <Box
      data-testid="transcribing-pill"
      role="status"
      aria-live="polite"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flex: 1,
        px: 1.5,
        py: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {[0, 1, 2].map(i => (
          <Box
            key={i}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: theme.palette.primary.main,
              animation: `${dotPulse} 1.2s ease-in-out ${i * 0.16}s infinite`,
            }}
          />
        ))}
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
        Transcribing your voice note...
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontVariantNumeric: 'tabular-nums',
          color: 'text.secondary',
          flexShrink: 0,
        }}
      >
        {formatElapsed(elapsedSec)}
      </Typography>
    </Box>
  );
}
